function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { Text } from "./Text";
import { View } from "./View";
import { useTheme } from "../styles";
var DEFAULT_DURATION_MS = 5000;
var SnackbarContext = /*#__PURE__*/ createContext({
    addSnackbarItem: function() {}
});
/**
 * @nosync
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-123}
 */ export function useSnackbarContext() {
    return useContext(SnackbarContext);
}
/**
 * Provides a facility for displaying "Snackbar" toast messages by providing a
 * `SnackbarContext` to child elements. The context includes `addSnackbarItem`
 * which can add an item to be displayed to the user.
 *
 * The provider displays the added content ensuring that no more than one item
 * is present at a time.
 *
 * @see {@link https://ouihealth.atlassian.net/browse/APPCORE-123}
 */ export function SnackbarProvider(param) {
    var children = param.children;
    var _useState = _sliced_to_array(useState([]), 2), items = _useState[0], setItems = _useState[1];
    var _useTheme = useTheme(), theme = _useTheme.theme, Shadow = _useTheme.Shadow;
    var opacity = useSharedValue(0);
    var addSnackbarItem = useCallback(function(item) {
        setItems(function(items) {
            return _to_consumable_array(items).concat([
                item
            ]);
        });
    }, []);
    var value = useMemo(function() {
        return {
            addSnackbarItem: addSnackbarItem
        };
    }, [
        addSnackbarItem
    ]);
    var currentItem = items[0];
    useEffect(function() {
        if (currentItem) {
            opacity.value = 1;
            var timeout;
            var _currentItem_durationMs;
            timeout = setTimeout(function() {
                opacity.value = 0;
                timeout = setTimeout(function() {
                    setItems(function(items) {
                        return items.slice(1);
                    });
                }, 800);
            }, (_currentItem_durationMs = currentItem.durationMs) !== null && _currentItem_durationMs !== void 0 ? _currentItem_durationMs : DEFAULT_DURATION_MS);
            return function() {
                return clearTimeout(timeout);
            };
        }
        return;
    }, [
        opacity,
        currentItem
    ]);
    var style = useAnimatedStyle(function() {
        return {
            opacity: withTiming(opacity.value, {
                duration: 500
            })
        };
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SnackbarContext.Provider, {
                value: value,
                children: children
            }),
            currentItem ? /*#__PURE__*/ _jsxs(Animated.View, {
                pointerEvents: "none",
                style: [
                    {
                        position: 'absolute',
                        width: '100%',
                        alignItems: 'center',
                        top: '15%'
                    },
                    style
                ],
                children: [
                    /*#__PURE__*/ _jsx(View, {
                        style: [
                            {
                                backgroundColor: theme.color.gray100,
                                // We need to do an opacity on the element, and not on the background color for android
                                // because otherwise the elevation shadow shows through the background and makes a weird
                                // looking border. To compensate, the displayed text is absoutely positioned outside this
                                // element and the inner text here is only for width purposes.
                                opacity: 0.7,
                                paddingVertical: 5,
                                paddingHorizontal: 10,
                                borderRadius: 5
                            },
                            Shadow.medium
                        ],
                        children: /*#__PURE__*/ _jsx(Text, {
                            text: currentItem.text,
                            color: "transparent",
                            size: 13,
                            lineHeight: 17,
                            role: "none"
                        })
                    }),
                    /*#__PURE__*/ _jsx(Text, {
                        text: currentItem.text,
                        color: "white",
                        size: 13,
                        lineHeight: 17,
                        style: {
                            position: 'absolute',
                            top: 5,
                            elevation: 4
                        }
                    })
                ]
            }) : null
        ]
    });
}
